import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useMaterialUIController } from "../../context";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useSelector } from "react-redux";
import {
  AddSlideCall,
  AddSubTitlesCall,
  AddTitlesCall,
  DeleteSubTitlesCall,
  DeleteTitlesCall,
  EditSubTitlesCall,
  EditTitlesCall,
  GetTitlesCall,
  SwapTitlesCall,
  EditTitleImageCall,
  UpdateTitleLockCall,
  UpdateTitleHiddenCall,
} from "../../redux/api/title.api";
import {
  translateTitleCall,
  translateSubTitleCall,
} from "../../redux/api/titleTranslation.api";
import TranslateConfirmDialog from "components/TranslateConfirmDialog";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import { Chip, Stack, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";
import { isEmpty } from "lodash";
import { slideThumbnailUrlFromSlide } from "./showTile/tileUrl";
import { useNavigate } from "react-router-dom";
import MDSnackbar from "components/MDSnackbar";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchIcon from "@mui/icons-material/Search";
import { openAppConfirm } from "../../components/AppDialogs";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { store } from "../../redux/store";
import axios from "axios";
import { basicURL } from "../../apiManager/APIConfig";
import "@google/model-viewer";
import SubtitleReferencedSlidesDnD from "../myLibrary/components/SubtitleReferencedSlidesDnD";

// Normalize 3D model URL like ShowThreeD (relative -> absolute with basicURL)
function getNormalizedModelUrl(slide) {
  const files = slide?.Files ?? slide?.files ?? [];
  const first = Array.isArray(files) ? files[0] : null;
  let url = first?.url ?? first?.path ?? null;
  if (url == null || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

function Titles({
  type,
  readOnly = false,
  library_category = null,
  embedded = false,
  addFromLibraryNode = null,
  itemsFromLibrary = null,
  onRemoveLibraryRef = null,
  onLibraryRefresh = null,
  /** (targetSubtitleId, orderedSlideIds) — My Library only; updates per-user link order, not public slides */
  onReorderLibraryRefs = null,
}) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const cardSurfaceBg = darkMode ? "rgba(255,255,255,0.06)" : "background.paper";
  const cardBorder = darkMode ? "rgba(255,255,255,0.12)" : "divider";
  const slideType = type;
  const three_d_model =
    type === "three_d_model" ||
    type === "emb_3d_model" ||
    type === "anatomy" ||
    library_category === "three_d_model" ||
    library_category === "emb_3d_model" ||
    library_category === "anatomy";
  const initialItems = useSelector((state) => {
    const st = state?.title;
    if (!st) return [];
    return Array.isArray(st.titles) ? st.titles : [];
  });
  const lang = useSelector((state) => state.general?.lang);
  const role = useSelector((state) => state?.auth?.role);
  const isAdmin = String(role || "").toLowerCase() === "admin";
  /*
    Lock toggle is only meaningful on the four core catalogue
    categories — these are the screens the mobile app paywalls based
    on the `subtitles[].locked` flag. We hide the lock UI everywhere
    else (presentations, library views, embedded pickers) so admins
    don't accidentally flip flags that nothing reads.
  */
  const LOCK_TOGGLE_TYPES = [
    "his",
    "emb",
    "three_d_model",
    "emb_3d_model",
    "anatomy",
  ];
  const showLockControls =
    isAdmin && !readOnly && LOCK_TOGGLE_TYPES.includes(slideType);
  /*
    Hide toggle uses the same scope as lock — only meaningful on the
    five core public catalogues (his / emb / 3d / emb_3d / anatomy).
    Hidden rows are filtered out for non-admin viewers on both web
    and mobile via `titles.is_hidden` on the server.
  */
  const showHideControls =
    isAdmin && !readOnly && LOCK_TOGGLE_TYPES.includes(slideType);
  const [fileRefs, setFileRefs] = useState({}); // store refs by subtitle ID
  const [lockPending, setLockPending] = useState({}); // { [subtitleId]: true }
  const [hidePending, setHidePending] = useState({}); // { [titleOrSubtitleId]: true }
  /*
    Per-row AI-translate confirmation dialog targets. We keep the
    full title / subtitle object so the dialog can show its current
    text in the warning copy without an extra fetch. Only one is
    open at a time — they're driven by separate keys so a parent
    title and one of its subtitles can't both be open.
  */
  const [translateTitleTarget, setTranslateTitleTarget] = useState(null);
  const [translateSubTitleTarget, setTranslateSubTitleTarget] = useState(null);

  // Defensive cleanup: strip stray ")}" text nodes that sometimes appear
  // between slide cards due to legacy content. This watcher keeps the DOM tidy
  // without affecting React state.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const sanitizeNode = (node) => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      const value = node.nodeValue;
      if (value && value.includes(")}")) {
        node.nodeValue = value.replace(/\)\}/g, "");
      }
    };

    // Initial pass
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n = walker.nextNode();
    while (n) {
      sanitizeNode(n);
      n = walker.nextNode();
    }

    // Observe future changes
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          sanitizeNode(m.target);
        }
        if (m.type === "childList") {
          m.addedNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              sanitizeNode(child);
            } else if (child.nodeType === Node.ELEMENT_NODE) {
              const walkerInner = document.createTreeWalker(child, NodeFilter.SHOW_TEXT);
              let t = walkerInner.nextNode();
              while (t) {
                sanitizeNode(t);
                t = walkerInner.nextNode();
              }
            }
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const rawItems = Array.isArray(initialItems) ? initialItems : [];
  // Treat missing library_category as "his" so existing private titles still show (e.g. in Histology tab)
  const filteredByCategory = library_category
    ? rawItems.filter((i) => (i.library_category || "his") === library_category)
    : rawItems;
  const [items, setItems] = useState(filteredByCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const displayItems = (embedded && itemsFromLibrary != null)
    ? (library_category ? itemsFromLibrary.filter((i) => (i.library_category || "his") === library_category) : itemsFromLibrary)
    : items;
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  // const [lang, setLanguage] = useState("en");

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  /** Swap order on server + Redux; embedded My Library also reloads props from GET /library. */
  const swapTitlesAndRefreshLibrary = useCallback(
    async (swapBody) => {
      await SwapTitlesCall(swapBody);
      if (embedded && typeof onLibraryRefresh === "function") {
        await onLibraryRefresh();
      }
    },
    [embedded, onLibraryRefresh]
  );

  /*
    Admin opts in to `?include_hidden=true` so the management page can
    still render the "Hidden" badge / toggle on hidden rows. Non-admin
    callers (e.g. a prof browsing this page for their own private
    titles) get the same filtered list the mobile app sees.
  */
  const fetchTitles = async () => {
    await GetTitlesCall(lang, type, isAdmin);
  };

  useEffect(() => {
    setItems([]);
    fetchTitles();
  }, [type, lang, isAdmin]);

  useEffect(() => {
    const raw = Array.isArray(initialItems) ? initialItems : [];
    const filtered = library_category ? raw.filter((i) => (i.library_category || "his") === library_category) : raw;
    setItems(filtered);
  }, [initialItems, library_category]);

  const handleAddTitle = () => {
    /*
      We deliberately don't seed `body` with a placeholder here:
      the `body` Localization is now surfaced as the model
      description on the mobile 3D viewer (via the floating
      "About this model" overlay), so a generated string like
      "Description 7" would pop up to students as if it were
      author-written. The admin fills it in via the inline
      `EditableDescription` once they have something to say.
    */
    AddTitlesCall({
      title: `New Title ${(displayItems || items)?.length + 1}`,
      lang,
      type,
      ...(library_category && { library_category }),
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleAddSubtitle = (itemId) => {
    AddSubTitlesCall({
      title: `New Subtitle ${(displayItems || items)?.length + 1}`,
      title_id: itemId,
      lang,
      type,
    }).then(() => { if (embedded && onLibraryRefresh) onLibraryRefresh(); });
  };

  const handleEdit = async (id, newValue, type) => {
    if (type === "MainTitle" || type === "MainBody") {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "MainTitle" ? "title" : "body"]: newValue,
      };
      await EditTitlesCall(body);
    } else {
      const body = {
        id,
        lang,
        type: slideType,
        [type === "SubTitle" ? "title" : "body"]: newValue,
      };
      await EditSubTitlesCall(body);
    }

    if (embedded && typeof onLibraryRefresh === "function") {
      await onLibraryRefresh();
    }
  };

  /*
    Admin-only: flip the lock state on a single subtitle (catalogue
    "slide"). Confirms first because this directly affects what
    paying users see in the mobile app:
      - locked  = true  → only subscribers / university users see it
      - locked  = false → everyone sees it (free)
    On success we let UpdateTitleLockCall refresh the Redux titles
    list so the badge + button icon update without a manual reload.
  */
  const handleToggleLock = async (subtitle) => {
    if (!showLockControls || !subtitle?.id) return;
    const nextLocked = !subtitle.locked;
    const confirmation = await openAppConfirm({
      title: nextLocked ? "Lock this slide?" : "Unlock this slide?",
      message: nextLocked
        ? "Locked slides are only visible to paying / university users in the app. Free users will see a paywall preview."
        : "Unlocked slides are free for every user in the app, including non-subscribers.",
      confirmText: nextLocked ? "Lock" : "Unlock",
      confirmColor: nextLocked ? "warning" : "success",
    });
    if (!confirmation) return;
    setLockPending((prev) => ({ ...prev, [subtitle.id]: true }));
    const res = await UpdateTitleLockCall({
      title_id: subtitle.id,
      locked: nextLocked,
      lang,
      type,
    });
    setLockPending((prev) => {
      const next = { ...prev };
      delete next[subtitle.id];
      return next;
    });
    if (res?.status && res.status >= 400) {
      setMessage(res?.message || "Failed to update lock state.");
      setTimeout(() => openErrorSB(), 200);
    } else {
      setMessage(nextLocked ? "Slide locked." : "Slide unlocked.");
      setTimeout(() => openSuccessSB(), 200);
    }
  };

  /*
    Admin-only: toggle `is_hidden` on a title or subtitle row.
    Hidden rows are filtered out for non-admin viewers on both web
    and mobile, so this is the way to take catalogue content offline
    without deleting it.

    `entity` is either a parent title (with `subtitles` array) or a
    subtitle row — both work because the backend resolves the root
    type via parent_id walk.
  */
  const handleToggleHidden = async (entity, kind = "title") => {
    if (!showHideControls || !entity?.id) return;
    const nextHidden = !entity.is_hidden;
    const noun = kind === "subtitle" ? "subtitle" : "title";
    const confirmation = await openAppConfirm({
      title: nextHidden ? `Hide this ${noun}?` : `Unhide this ${noun}?`,
      message: nextHidden
        ? `Hidden ${noun}s are removed from the catalogue for everyone except admins (web + mobile). Admins can unhide them at any time.`
        : `Unhidden ${noun}s become visible again to all users in the catalogue.`,
      confirmText: nextHidden ? "Hide" : "Unhide",
      confirmColor: nextHidden ? "warning" : "success",
    });
    if (!confirmation) return;
    setHidePending((prev) => ({ ...prev, [entity.id]: true }));
    const res = await UpdateTitleHiddenCall({
      title_id: entity.id,
      is_hidden: nextHidden,
      lang,
      type,
    });
    setHidePending((prev) => {
      const next = { ...prev };
      delete next[entity.id];
      return next;
    });
    if (res?.status && res.status >= 400) {
      setMessage(res?.message || "Failed to update hidden state.");
      setTimeout(() => openErrorSB(), 200);
    } else {
      setMessage(
        nextHidden
          ? `${noun.charAt(0).toUpperCase() + noun.slice(1)} hidden.`
          : `${noun.charAt(0).toUpperCase() + noun.slice(1)} unhidden.`
      );
      setTimeout(() => openSuccessSB(), 200);
    }
  };

  const handleDeleteSubTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: "Delete subtitle?",
      message:
        "Are you sure you want to delete this subtitle? All pins, texts and slides inside it will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteSubTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The subtitle deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };
  const handleDeleteTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: "Delete title?",
      message:
        "Are you sure you want to delete this title and all its subtitles? All pins, texts and slides inside will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The title deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
        if (embedded && typeof onLibraryRefresh === "function") {
          onLibraryRefresh();
        }
      }
    }
  };

  const handleAddMaterial = async (id) => {
    const res = await AddSlideCall({ title_id: id, lang, type });
    if (!res?.slide?.id) return;
    const base = three_d_model
      ? `/showThreeD/${res.slide.id}/${lang}`
      : `/showTitle/${res.slide.id}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlide = (subtitle) => {
    const primary =
      (subtitle?.slides && subtitle.slides[0]) ||
      (subtitle?.referencedSlides && subtitle.referencedSlides[0]) ||
      null;
    const slideId = primary?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const handleViewSlideById = (slide) => {
    const slideId = slide?.id;
    if (!slideId) return;
    const base = three_d_model
      ? `/showThreeD/${slideId}/${lang}`
      : `/showTitle/${slideId}/${lang}`;
    const url = embedded ? `${base}?private=1` : base;
    navigate(url);
  };

  const getFileRef = (id) => {
    if (!fileRefs[id]) {
      fileRefs[id] = React.createRef(); // create ref if not exists
    }
    return fileRefs[id];
  };

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim();

  const getLocalizedField = (entity, field) => {
    const list = Array.isArray(entity?.titleLocalizations)
      ? entity.titleLocalizations.filter((e) => e?.field === field)
      : [];
    return list?.[0]?.value || "";
  };

  const visibleItems = useMemo(() => {
    const q = normalizeText(searchQuery);
    if (!q) return Array.isArray(displayItems) ? displayItems : [];

    return (Array.isArray(displayItems) ? displayItems : []).filter((item) => {
      const mainTitle = normalizeText(getLocalizedField(item, "title"));
      const mainBody = normalizeText(getLocalizedField(item, "body"));
      const inMain = mainTitle.includes(q) || mainBody.includes(q);
      if (inMain) return true;

      const subtitles = Array.isArray(item?.subtitles) ? item.subtitles : [];
      return subtitles.some((subtitle) => {
        const subTitle = normalizeText(getLocalizedField(subtitle, "title"));
        const subBody = normalizeText(getLocalizedField(subtitle, "body"));
        return subTitle.includes(q) || subBody.includes(q);
      });
    });
  }, [displayItems, searchQuery]);

  const handleAddThumbNail = async (event, id) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Handle `.zip` file upload
        const formdata = new FormData();
        formdata.append("file", file);
        formdata.append("entity_type", "public");
        formdata.append("entity_id", "3989785d-49d1-49da-9373-0ed41c1a76bd");
        formdata.append("destination", "public");
        formdata.append("folder_name", "public");

        const access_token = store.getState().auth?.access_token;
        let myHeaders = new Headers();
        myHeaders.append("Authorization", access_token);

        myHeaders = {
          Authorization: access_token,
        };

        const response = await axios.post(`${basicURL}/file/upload`, formdata, {
          headers: myHeaders,
          onUploadProgress: (progressEvent) => {
            // Calculate the progress percentage
            // const percent = Math.round(
            //   (progressEvent.loaded / progressEvent.total) * 100
            // );
          },
        });
        console.log("pippo response", response);
        const thumbnail = response?.data?.download_url;
        console.log("ZIP file uploaded:", thumbnail);
        const body = {
          id,
          lang,
          type: slideType,
          image: thumbnail,
        };
        console.log("ZIP file body:", body);

        EditTitleImageCall(body);

        // window.location.reload();
      } catch (error) {
        console.error("Upload error:", error);
      }
    }
  };

  const renderItem = (item, index, list = items) => {
    const filteredTitle = item?.titleLocalizations?.filter(
      (e) => e.field == "title"
    );
    const filteredBody = item?.titleLocalizations?.filter(
      (e) => e.field == "body"
    );

    const title = filteredTitle?.[0]?.value || "Click to edit the title";
    const body = filteredBody?.[0]?.value || "Click to edit the body";
    const gapPx = 15;
    return (
      <MDBox display="flex" flexDirection="column" alignItems="flex-start" sx={{ mb: 0, px: `${gapPx}px`, pt: 0, pb: 0.5 }}>
          {!readOnly && (
            <MDBox display="flex" gap={0.5} sx={{ mb: `${gapPx}px` }}>
              {index !== 0 && (
                <Tooltip title="Move up">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index - 1]?.id,
                        lang,
                        type,
                      };
                      swapTitlesAndRefreshLibrary(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {list?.length !== index + 1 && (
                <Tooltip title="Move down">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const body = {
                        current_id: item?.id,
                        goal_id: list?.[index + 1]?.id,
                        lang,
                        type,
                      };
                      swapTitlesAndRefreshLibrary(body);
                    }}
                    sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          )}

          <EditableTitle
            title={title}
            titleFont="title"
            onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
            readOnly={readOnly}
          />
          {/* Description / "about this title" body. The mobile app
              renders this string in the floating overlay above the
              3D viewer, so admins can use it to set context for the
              whole catalogue node (e.g. "Histology — connective
              tissues" gets a paragraph explaining the chapter). */}
          <EditableDescription
            description={item?.titleLocalizations?.find((e) => e?.field === "body")?.value || ""}
            onEdit={(newValue) => handleEdit(item.id, newValue, "MainBody")}
            readOnly={readOnly}
            variant="title"
          />
          {!readOnly && (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} width="100%" sx={{ mt: `${gapPx}px` }}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDButton
                  color="primary"
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => handleAddSubtitle(item.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 3,
                    px: 2,
                    boxShadow: "none",
                    "&:hover": { boxShadow: theme.shadows[2] },
                  }}
                >
                  {embedded ? "Add Slide" : "Add Subtitle"}
                </MDButton>
                {embedded && (typeof addFromLibraryNode === "function" ? addFromLibraryNode(item) : addFromLibraryNode)}
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                {/* AI Translate — translates the title text + the
                    long-form description (body) into every supported
                    app locale in one shot. UPSERT on the backend,
                    so re-clicking refreshes existing translations. */}
                <Tooltip title="AI Translate — translate this title and its description into every supported language. Re-running overwrites prior translations.">
                  <span>
                    <MDButton
                      color="primary"
                      variant="outlined"
                      startIcon={<TranslateRoundedIcon />}
                      onClick={() => setTranslateTitleTarget(item)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 3,
                        borderWidth: 1.5,
                        "&:hover": { borderWidth: 1.5 },
                      }}
                    >
                      AI Translate
                    </MDButton>
                  </span>
                </Tooltip>
                {/* Hide / Unhide the whole parent title (admin-only on
                    core catalogue types). When hidden, the row + all
                    its subtitles disappear from non-admin views on
                    web and mobile. Admins keep seeing it (greyed out
                    via the icon state) so they can flip it back. */}
                {showHideControls && (
                  <Tooltip
                    title={
                      item?.is_hidden
                        ? "Unhide — show this title to all users again"
                        : "Hide — remove this title from non-admin views"
                    }
                  >
                    <span>
                      <MDButton
                        color={item?.is_hidden ? "warning" : "info"}
                        variant="outlined"
                        startIcon={
                          <Icon>
                            {item?.is_hidden ? "visibility_off" : "visibility"}
                          </Icon>
                        }
                        onClick={() => handleToggleHidden(item, "title")}
                        disabled={!!hidePending[item.id]}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 3,
                          borderWidth: 1.5,
                          "&:hover": { borderWidth: 1.5 },
                        }}
                      >
                        {item?.is_hidden ? "Unhide" : "Hide"}
                      </MDButton>
                    </span>
                  </Tooltip>
                )}
                <MDButton
                  color="error"
                  variant="outlined"
                  onClick={() => handleDeleteTitle(item.id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 3,
                    borderWidth: 1.5,
                    "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                  }}
                >
                  Delete
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {item?.subtitles?.length > 0 && (
            <MDBox sx={{ mt: `${gapPx}px`, display: "flex", flexWrap: "wrap", gap: "20px" }}>
              {item.subtitles.map((subtitle, idx) => {
              const fileInputRef = getFileRef(subtitle.id);

              const ownSlides = subtitle?.slides || [];
              const refSlides = subtitle?.referencedSlides || [];
              const primarySlide = ownSlides[0] || refSlides[0] || null;
              const hasAnySlide = !!primarySlide;
              const label = hasAnySlide ? "View" : "Add Material";

              const filteredTitle = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "title"
              );
              const filteredBody = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "body"
              );

              const title =
                filteredTitle?.[0]?.value || "Click to edit the sub title";
              const body =
                filteredBody?.[0]?.value || "Click to edit the sub body";
              return (
                <React.Fragment key={subtitle.id}>
                <MDBox sx={{ width: 224, minWidth: 196, flex: "0 0 auto", position: "relative" }}>
                  {/* Lock-status badge (admin-only, catalogue slides only).
                      Sits on top-left of the card, above the thumbnail.
                      Green "Free" / amber "Locked" gives admins an at-a-
                      glance view of what's behind the paywall. */}
                  {showLockControls && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        backdropFilter: "blur(6px)",
                        backgroundColor: subtitle?.locked
                          ? alpha(theme.palette.warning.main, 0.92)
                          : alpha(theme.palette.success.main, 0.92),
                        color: "#fff",
                        boxShadow: theme.shadows[2],
                        pointerEvents: "none",
                      }}
                    >
                      <Icon sx={{ fontSize: "0.9rem !important" }}>
                        {subtitle?.locked ? "lock" : "lock_open"}
                      </Icon>
                      {subtitle?.locked ? "Locked" : "Free"}
                    </MDBox>
                  )}
                  {/* Hidden-status badge (admin-only). Only shown when
                      the subtitle is currently hidden — gives admins
                      a clear at-a-glance signal that this card is
                      invisible to everyone else. */}
                  {showHideControls && subtitle?.is_hidden && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        backdropFilter: "blur(6px)",
                        backgroundColor: alpha(theme.palette.warning.main, 0.92),
                        color: "#fff",
                        boxShadow: theme.shadows[2],
                        pointerEvents: "none",
                      }}
                    >
                      <Icon sx={{ fontSize: "0.9rem !important" }}>
                        visibility_off
                      </Icon>
                      Hidden
                    </MDBox>
                  )}
                  <Card
                    variant="outlined"
                    sx={{
                      pt: 0,
                      px: 0,
                      pb: `${gapPx}px`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      borderRadius: 3,
                      borderColor: cardBorder,
                      bgcolor: cardSurfaceBg,
                      color: "text.main",
                      boxShadow: theme.shadows[1],
                      overflow: "hidden",
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": { boxShadow: theme.shadows[4], borderColor: theme.palette.divider },
                      "& > *:first-of-type": { margin: 0, padding: 0, width: "100%", maxWidth: "100%" },
                    }}
                  >
                    {(three_d_model && !subtitle?.image && primarySlide && getNormalizedModelUrl(primarySlide)) ? (
                      <MDBox
                        sx={{
                          width: "100%",
                          height: 126,
                          overflow: "hidden",
                          margin: 0,
                          padding: 0,
                          flexShrink: 0,
                          bgcolor: "background.elevated",
                        }}
                      >
                        <iframe
                          src={
                            typeof window !== "undefined" && primarySlide
                              ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(getNormalizedModelUrl(primarySlide))}&theme=${darkMode ? "dark" : "light"}`
                              : ""
                          }
                          title={`3D model: ${title}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            display: "block",
                          }}
                        />
                      </MDBox>
                    ) : (() => {
                      const slideImageUrl =
                        subtitle?.image ?? slideThumbnailUrlFromSlide(primarySlide);
                      const isEmptySlide = slideImageUrl == null;
                      const isDark = darkMode;
                      return (
                        <div
                          className="titles-card-thumb"
                          style={{
                            position: "relative",
                            width: "100%",
                            height: 126,
                            overflow: "hidden",
                            margin: 0,
                            padding: 0,
                            lineHeight: 0,
                            flexShrink: 0,
                            background: isEmptySlide
                              ? (isDark
                                  ? "linear-gradient(160deg, rgba(35,38,42,0.99) 0%, rgba(22,24,28,0.99) 50%, rgba(18,20,24,0.99) 100%)"
                                  : "linear-gradient(160deg, rgba(250,251,253,0.99) 0%, rgba(240,242,246,0.99) 50%, rgba(232,235,241,0.99) 100%)")
                              : (theme.palette.background?.elevated || "#1a1a1a"),
                            direction: "ltr",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isEmptySlide ? (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              gap={1.25}
                              sx={{
                                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.42)",
                              }}
                            >
                              <MDBox
                                sx={{
                                  width: 72,
                                  height: 72,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                  border: "1px solid",
                                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                  boxShadow: isDark
                                    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                                    : "inset 0 1px 0 rgba(255,255,255,0.6)",
                                }}
                              >
                                <Icon
                                  sx={{
                                    fontSize: 44,
                                    opacity: 0.85,
                                  }}
                                >
                                  photo_library
                                </Icon>
                              </MDBox>
                              <MDTypography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                  opacity: 0.95,
                                  fontSize: "0.7rem",
                                }}
                              >
                                Empty slide
                              </MDTypography>
                            </MDBox>
                          ) : (
                            <img
                              src={slideImageUrl}
                              alt={title}
                              draggable={false}
                              style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center top",
                                margin: 0,
                                padding: 0,
                                display: "block",
                                boxSizing: "border-box",
                              }}
                            />
                          )}
                        </div>
                      );
                    })()}

                    <MDBox width="100%" display="flex" flexDirection="column" alignItems="flex-start" sx={{ minWidth: 0, px: `${gapPx}px` }}>
                      <EditableTitle
                        title={title}
                        titleFont="subtitle"
                        onEdit={(newTitle) =>
                          handleEdit(subtitle.id, newTitle, "SubTitle")
                        }
                        readOnly={readOnly}
                      />
                      {/* Per-slide / per-3D-model description — what
                          actually pops up over the 3D viewer in the
                          mobile app. We map it to the SubTitle's
                          `body` Localization so it survives existing
                          `handleEdit` plumbing without any extra
                          endpoints. */}
                      <EditableDescription
                        description={subtitle?.titleLocalizations?.find((e) => e?.field === "body")?.value || ""}
                        onEdit={(newValue) =>
                          handleEdit(subtitle.id, newValue, "SubBody")
                        }
                        readOnly={readOnly}
                        variant="subtitle"
                      />

                      {!readOnly && (
                        <MDBox display="flex" gap={0.5} sx={{ my: `${gapPx}px` }}>
                          {idx !== 0 && (
                            <Tooltip title="Move left">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx - 1]?.id,
                                    lang,
                                    type,
                                  };
                                  swapTitlesAndRefreshLibrary(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowLeftIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item?.subtitles?.length !== idx + 1 && (
                            <Tooltip title="Move right">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const body = {
                                    current_id: subtitle?.id,
                                    goal_id: item?.subtitles?.[idx + 1]?.id,
                                    lang,
                                    type,
                                  };
                                  swapTitlesAndRefreshLibrary(body);
                                }}
                                sx={{ color: "text.secondary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" } }}
                              >
                                <ArrowRightIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      )}
                      <MDBox display="flex" width="100%" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: `${gapPx}px` }}>
                        {!readOnly && (
                          <>
                            <MDButton
                              color="error"
                              variant="outlined"
                              size="small"
                              onClick={() => handleDeleteSubTitle(subtitle.id)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 3,
                                px: 1.5,
                                borderWidth: 1.5,
                                "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06) },
                              }}
                            >
                              Delete
                            </MDButton>
                            <input
                              type="file"
                              ref={fileInputRef}
                              style={{ display: "none" }}
                              onChange={(event) =>
                                handleAddThumbNail(event, subtitle.id)
                              }
                            />
                            <Tooltip title="Change thumbnail">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                  borderRadius: 2,
                                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                                }}
                              >
                                <Icon fontSize="small">image</Icon>
                              </IconButton>
                            </Tooltip>
                            {/* AI Translate — one-click translate the
                                subtitle's card label + its description
                                (the overlay shown above the slide / 3D
                                viewer in the mobile app) into every
                                supported locale. UPSERT on the backend
                                — clicking again refreshes translations. */}
                            <Tooltip title="AI Translate this subtitle and its description into every supported language. Re-running overwrites prior translations.">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setTranslateSubTitleTarget({
                                    ...subtitle,
                                    _displayTitle: title,
                                  })
                                }
                                sx={{
                                  borderRadius: 2,
                                  color: "primary.main",
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.06,
                                  ),
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.16,
                                    ),
                                  },
                                }}
                                aria-label="AI translate this subtitle into every supported language"
                              >
                                <TranslateRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Lock / Unlock toggle (admin-only). Drives
                                titles.locked which the mobile app reads
                                in resolveSubtitleNavigationDecision to
                                gate access behind the paywall. */}
                            {showLockControls && (
                              <Tooltip
                                title={
                                  subtitle?.locked
                                    ? "Unlock — make this slide free for everyone"
                                    : "Lock — require a subscription to view this slide"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleLock(subtitle)}
                                    disabled={!!lockPending[subtitle.id]}
                                    sx={{
                                      borderRadius: 2,
                                      color: subtitle?.locked
                                        ? "warning.main"
                                        : "success.main",
                                      bgcolor: subtitle?.locked
                                        ? alpha(theme.palette.warning.main, 0.08)
                                        : alpha(theme.palette.success.main, 0.08),
                                      "&:hover": {
                                        bgcolor: subtitle?.locked
                                          ? alpha(theme.palette.warning.main, 0.16)
                                          : alpha(theme.palette.success.main, 0.16),
                                      },
                                    }}
                                  >
                                    <Icon fontSize="small">
                                      {subtitle?.locked ? "lock" : "lock_open"}
                                    </Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {/* Hide / Unhide toggle (admin-only). Sets
                                titles.is_hidden, which the backend uses
                                to filter rows out of every non-admin
                                listing endpoint (web + mobile). */}
                            {showHideControls && (
                              <Tooltip
                                title={
                                  subtitle?.is_hidden
                                    ? "Unhide — show this subtitle to all users again"
                                    : "Hide — remove this subtitle from non-admin views"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleToggleHidden(subtitle, "subtitle")
                                    }
                                    disabled={!!hidePending[subtitle.id]}
                                    sx={{
                                      borderRadius: 2,
                                      color: subtitle?.is_hidden
                                        ? "warning.main"
                                        : "info.main",
                                      bgcolor: subtitle?.is_hidden
                                        ? alpha(theme.palette.warning.main, 0.08)
                                        : alpha(theme.palette.info.main, 0.08),
                                      "&:hover": {
                                        bgcolor: subtitle?.is_hidden
                                          ? alpha(theme.palette.warning.main, 0.16)
                                          : alpha(theme.palette.info.main, 0.16),
                                      },
                                    }}
                                  >
                                    <Icon fontSize="small">
                                      {subtitle?.is_hidden
                                        ? "visibility_off"
                                        : "visibility"}
                                    </Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </>
                        )}
                        <MDButton
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={() =>
                            readOnly || hasAnySlide
                              ? handleViewSlide(subtitle)
                              : handleAddMaterial(subtitle.id)
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 3,
                            px: 1.75,
                            boxShadow: "none",
                            "&:hover": { boxShadow: theme.shadows[2] },
                          }}
                        >
                          {readOnly ? "View" : label}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
                {(() => {
                  const extraRefSlides =
                    ownSlides.length > 0 ? refSlides : refSlides.slice(1);
                  const renderLinkedSlideCard = (refSlide) => {
                    const refThumbUrl = slideThumbnailUrlFromSlide(refSlide);
                    const slideTitle =
                      refSlide?.slide_title ||
                      refSlide?.slideLocalizations?.find((l) => l.field === "title")?.value?.trim() ||
                      refSlide?.slide_localizations?.find((l) => l.field === "title")?.value?.trim();
                    const refLabel = slideTitle || "Slide";
                    const isDark = darkMode;
                    return (
                      <MDBox sx={{ width: 224, minWidth: 196, flex: "0 0 auto" }}>
                        <Card
                          variant="outlined"
                          sx={{
                            pt: 0,
                            px: 0,
                            pb: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 3,
                            borderColor: cardBorder,
                            bgcolor: cardSurfaceBg,
                            boxShadow: theme.shadows[1],
                            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                            "&:hover": {
                              boxShadow: theme.shadows[4],
                              borderColor: alpha(theme.palette.primary.main, 0.35),
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: 126,
                              overflow: "hidden",
                              flexShrink: 0,
                              background: refThumbUrl
                                ? undefined
                                : isDark
                                  ? "rgba(0,0,0,0.2)"
                                  : "rgba(0,0,0,0.06)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {refThumbUrl ? (
                              <Box
                                component="img"
                                src={refThumbUrl}
                                alt={refLabel}
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  objectPosition: "center top",
                                }}
                              />
                            ) : (
                              <Icon sx={{ fontSize: 36, color: "text.secondary" }}>photo_library</Icon>
                            )}
                          </Box>
                          <MDBox sx={{ px: `${gapPx}px`, py: 1.25 }}>
                            <MDTypography
                              variant="body2"
                              fontWeight={600}
                              sx={{ display: "block", mb: 1.25, lineHeight: 1.3 }}
                            >
                              {refLabel}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                              <MDButton
                                color="primary"
                                variant="contained"
                                size="small"
                                onClick={() => handleViewSlideById(refSlide)}
                                startIcon={<Icon sx={{ fontSize: 18 }}>visibility</Icon>}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: 3,
                                  px: 1.5,
                                  boxShadow: "none",
                                  "&:hover": { boxShadow: theme.shadows[2] },
                                }}
                              >
                                View
                              </MDButton>
                              {embedded && onRemoveLibraryRef && refSlide.public_title_id && (
                                <Tooltip title="Remove from library">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      onRemoveLibraryRef(
                                        refSlide.public_title_id,
                                        refSlide.id,
                                        subtitle.id
                                      )
                                    }
                                    sx={{
                                      borderRadius: 2,
                                      bgcolor: alpha(theme.palette.error.main, 0.06),
                                      "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) },
                                    }}
                                  >
                                    <Icon sx={{ fontSize: 20 }}>link_off</Icon>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </MDBox>
                          </MDBox>
                        </Card>
                      </MDBox>
                    );
                  };
                  if (!extraRefSlides.length) return null;
                  if (embedded && onReorderLibraryRefs && !readOnly) {
                    return (
                      <SubtitleReferencedSlidesDnD
                        subtitleId={subtitle.id}
                        slides={extraRefSlides}
                        droppableId={`lib-refs-${subtitle.id}`}
                        onReorder={onReorderLibraryRefs}
                        renderSlide={renderLinkedSlideCard}
                      />
                    );
                  }
                  return extraRefSlides.map((refSlide) => (
                    <React.Fragment key={refSlide.id}>{renderLinkedSlideCard(refSlide)}</React.Fragment>
                  ));
                })()}
                </React.Fragment>
              );
            })}
            </MDBox>
          )}
      </MDBox>
    );
  };

  const content = (
    <>
      <MDBox py={1} mb={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <MDBox display="flex" alignItems="center" gap={2}>
          {!readOnly && (
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => handleAddTitle()}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Add Title
            </MDButton>
          )}
        </MDBox>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${three_d_model ? "3D models" : "tiles"}...`}
          size="small"
          sx={{
            minWidth: 240,
            maxWidth: 360,
            width: "100%",
            "& .MuiInputBase-root": {
              borderRadius: 999,
              bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "background.paper",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  sx={{ mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </MDBox>
      <MDBox py={0} width="100%">
        <Grid container spacing={0.5}>
          {Array.isArray(visibleItems) && visibleItems.length > 0 &&
            visibleItems.map((item, index) => (
              <Grid item xs={12} key={item.id}>
                {renderItem(item, index, visibleItems)}
              </Grid>
            ))}
          {Array.isArray(visibleItems) && visibleItems.length === 0 && (
            <Grid item xs={12}>
              <MDBox
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <MDTypography variant="button">
                  No results found for "{searchQuery}".
                </MDTypography>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </MDBox>
      {/* AI translate confirmation — parent Title row.
          Translates the title's `title` + `body` Localization rows
          into every supported app locale. UPSERT on the backend, so
          re-running this dialog overwrites prior translations
          (which is exactly the "click again to refresh" flow). */}
      <TranslateConfirmDialog
        open={!!translateTitleTarget}
        onClose={() => setTranslateTitleTarget(null)}
        title="Translate title and description"
        description={
          translateTitleTarget ? (
            <>
              The title{" "}
              <strong>
                {translateTitleTarget?.titleLocalizations?.find(
                  (e) => e?.field === "title",
                )?.value || "(untitled)"}
              </strong>{" "}
              and its description will be translated into every
              supported app language using AI.
            </>
          ) : null
        }
        targetLangsLabel="English, Portuguese, Spanish, Italian, German, Dutch, Chinese, Japanese, Arabic (source locale is skipped)"
        itemSummaryLabel="Title + description"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating title and description…");
          const res = await translateTitleCall(translateTitleTarget?.id);
          // Refresh the Redux titles list so the inline editors pick
          // up the freshly-written localizations for the active lang.
          if (res?.status === 200) await fetchTitles();
          return res;
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Translated into {res?.targetLangs?.length ?? 0} language
              {res?.targetLangs?.length === 1 ? "" : "s"}
              {typeof res?.created === "number" || typeof res?.updated === "number"
                ? ` · ${res?.created ?? 0} created, ${res?.updated ?? 0} updated`
                : ""}
              .
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={String(code).toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
      {/* AI translate confirmation — SubTitle (slide entry). Same
          UPSERT semantics as the parent variant, but scoped to one
          subtitle's `title` + `body` Localizations. */}
      <TranslateConfirmDialog
        open={!!translateSubTitleTarget}
        onClose={() => setTranslateSubTitleTarget(null)}
        title="Translate subtitle and description"
        description={
          translateSubTitleTarget ? (
            <>
              The subtitle{" "}
              <strong>
                {translateSubTitleTarget?._displayTitle ||
                  translateSubTitleTarget?.titleLocalizations?.find(
                    (e) => e?.field === "title",
                  )?.value ||
                  "(untitled)"}
              </strong>{" "}
              and its description will be translated into every
              supported app language using AI.
            </>
          ) : null
        }
        targetLangsLabel="English, Portuguese, Spanish, Italian, German, Dutch, Chinese, Japanese, Arabic (source locale is skipped)"
        itemSummaryLabel="Subtitle + description"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating subtitle and description…");
          const res = await translateSubTitleCall(
            translateSubTitleTarget?.id,
          );
          if (res?.status === 200) await fetchTitles();
          return res;
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Translated into {res?.targetLangs?.length ?? 0} language
              {res?.targetLangs?.length === 1 ? "" : "s"}
              {typeof res?.created === "number" || typeof res?.updated === "number"
                ? ` · ${res?.created ?? 0} created, ${res?.updated ?? 0} updated`
                : ""}
              .
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={String(code).toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
      <MDSnackbar
        color="success"
        icon="check"
        title={message}
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title={message}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
      {!embedded && <Footer />}
    </>
  );

  if (embedded) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
}

const capitalizeFirst = (str) =>
  str && typeof str === "string" && str.length
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : str;

/*
  Inline-editable, multi-line "description" field.

  Lives on the same Localization row as `title` / `body` (the existing
  schema) — `editKind` ('MainBody' | 'SubBody') tells the parent which
  endpoint to hit (`EditTitlesCall` vs `EditSubTitlesCall`). On the
  mobile app the same string is surfaced in the floating
  `Interactive3DViewerDescription` overlay above the 3D viewer, so
  whatever an admin writes here is what students read on the model.

  Visual behaviour:
    • Read-only mode: hides itself entirely when there's no
      description, so old content stays clean.
    • Editable mode (admin / owner):
        - Empty       → muted "Add a description…" hint button.
        - Non-empty   → muted preview text.
        - Click       → expands into a multi-line textarea.
        - Blur / Save → fires `onEdit(value)`.

  We don't autosave on every keystroke because the upstream API
  re-fetches the whole title list after each save — typing 100
  characters would mean 100 GETs.
*/
/*
  When the description on a card is long, we clamp it to this many
  visual lines and surface a "Show more" / "Show less" toggle
  underneath. Two lines is the sweet spot — enough to give a
  preview without letting a paragraph hijack the card height — and
  matches the rest of the catalogue UI's body-text clamp.
*/
const DESCRIPTION_CLAMP_LINES = 2;

const EditableDescription = ({
  description,
  onEdit,
  readOnly = false,
  variant = 'title',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(description || "");
  /*
    Truncation state: `expanded` is the user's manual choice to see
    the full text, `isOverflow` is the measured truth ("does this
    text actually overflow at 2 lines?"). We only render the
    "Show more" toggle when both the description has content AND
    the text is actually overflowing — short descriptions never
    show the toggle, so the card stays clean.
  */
  const [expanded, setExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const textRef = useRef(null);

  /* When the parent finishes refetching after a save the
     `description` prop reference may change — keep the local draft
     in sync so the next "click-to-edit" starts from the latest
     server value rather than a stale draft. Resetting `expanded`
     too means a freshly edited (potentially much shorter) value
     re-checks overflow with a clean slate. */
  useEffect(() => {
    setDraft(description || "");
    setExpanded(false);
  }, [description]);

  /*
    Measure overflow whenever the rendered text changes (description
    update, variant flip, expanded toggle). We compare scrollHeight
    against clientHeight: `-webkit-line-clamp` constrains
    clientHeight to the visible 2-line box, and scrollHeight reports
    the full natural height — anything over the rounded threshold
    means there's hidden content and the toggle is warranted.
  */
  useEffect(() => {
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;
    /* +1 px tolerance to absorb sub-pixel rounding in MUI/Webkit
       so a perfectly-2-line description doesn't false-positive. */
    setIsOverflow(el.scrollHeight - 1 > el.clientHeight);
  }, [description, expanded, variant, readOnly]);

  const fontSize = variant === 'subtitle' ? '0.8125rem' : '0.875rem';

  const finish = () => {
    setIsEditing(false);
    const trimmed = (draft || "").trim();
    if (trimmed !== (description || "").trim()) {
      onEdit(trimmed);
    }
  };

  /*
    Shared text + toggle renderer used by both readOnly and idle
    edit-on-click branches below. Centralises the line-clamp +
    "Show more / Show less" plumbing so the two viewer experiences
    behave identically.
  */
  const renderClampedText = (text, hasContent) => (
    <>
      <MDTypography
        ref={textRef}
        variant="caption"
        sx={{
          color: hasContent ? "text.secondary" : "text.disabled",
          fontStyle: hasContent ? "normal" : "italic",
          fontSize,
          fontWeight: 400,
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          textAlign: "center",
          width: "100%",
          display: expanded ? "block" : "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : DESCRIPTION_CLAMP_LINES,
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {text}
      </MDTypography>
      {hasContent && (isOverflow || expanded) && (
        <MDBox
          /*
             Stop the click from bubbling up to the wrapper's
             onClick — otherwise toggling "Show more" on the
             editable variant would also flip the row into edit
             mode. The button still works inside the readOnly
             branch where the wrapper has no onClick.
          */
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((v) => !v);
            }
          }}
          sx={{
            mt: 0.5,
            display: "inline-flex",
            alignSelf: "center",
            cursor: "pointer",
            color: "info.main",
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "none",
            "&:hover": {
              textDecoration: "underline",
            },
            outline: "none",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </MDBox>
      )}
    </>
  );

  if (readOnly) {
    if (!description) return null;
    return (
      <MDBox
        sx={{
          mt: 0.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {renderClampedText(description, true)}
      </MDBox>
    );
  }

  if (isEditing) {
    return (
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          /* Cmd/Ctrl-Enter saves & exits — Enter alone keeps the
             newline so authors can write paragraph descriptions
             without us hijacking line breaks. */
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            finish();
          }
          if (e.key === "Escape") {
            setDraft(description || "");
            setIsEditing(false);
          }
        }}
        autoFocus
        multiline
        minRows={2}
        maxRows={6}
        placeholder="Write a description for this model — students see this above the 3D viewer."
        size="small"
        fullWidth
        sx={{
          mt: 0.5,
          "& .MuiInputBase-input": {
            fontSize,
            lineHeight: 1.5,
          },
        }}
      />
    );
  }

  /* Idle state: muted preview + click affordance. Empty descriptions
     get a soft "Add description…" hint so admins know the field
     exists without it competing with the title for attention. */
  const hasDescription = !!(description && description.trim());
  return (
    <MDBox
      onClick={() => setIsEditing(true)}
      sx={{
        mt: 0.5,
        width: "100%",
        cursor: "pointer",
        borderRadius: 1,
        px: 0.75,
        py: 0.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        transition: "background-color 120ms ease",
        "&:hover": {
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        },
      }}
    >
      {renderClampedText(
        hasDescription ? description : "Add a description…",
        hasDescription,
      )}
    </MDBox>
  );
};

const EditableTitle = ({ title, onEdit, titleFont, readOnly = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const displayTitle = capitalizeFirst(title);
  let fontSize = "1rem";
  let fontWight = 400;

  if (titleFont === "title") {
    fontSize = "1.4rem";
    fontWight = "bold";
  } else if (titleFont === "subtitle") {
    fontSize = "0.9375rem";
    fontWight = 600;
  } else if (titleFont === "body") {
    fontSize = "0.875rem";
    fontWight = 400;
  }
  const handleBlur = () => {
    setIsEditing(false);
    onEdit(currentTitle);
  };

  if (readOnly) {
    return (
      <MDTypography
        variant="h6"
        style={{ textAlign: "center", fontSize, fontWight }}
      >
        {displayTitle}
      </MDTypography>
    );
  }

  return isEditing ? (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <input
        type="text"
        value={currentTitle}
        onChange={(e) => setCurrentTitle(e.target.value)}
        onBlur={handleBlur}
        autoFocus
        style={{
          fontSize,
          fontWight,
          textAlign: "center",
          padding: "5px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          width: "80%",
          marginRight: "10px",
        }}
      />
      <MDButton
        size={"10px"}
        color="success"
        variant="contained"
        onClick={() => {
          handleBlur();
          onEdit();
        }}
      >
        <Icon fontSize="medium">check</Icon>
      </MDButton>
    </div>
  ) : (
    <MDTypography
      variant="h6"
      onClick={() => setIsEditing(true)}
      style={{ cursor: "pointer", textAlign: "center", fontSize, fontWight }}
    >
      {displayTitle}
    </MDTypography>
  );
};

export default Titles;
